import React, { useEffect, useState } from 'react';
import PageHeaderLayout from '../../../../components/Layout/PageHeaderLayout';
import { Box, Button, Card, Chip, Grid, TextField, Typography } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Add, ArrowBack, Edit, ListAlt } from '@mui/icons-material';
import { PermissionGuard } from '../../../../components/PermissionGuard';
import { SectionServices } from './SectionServices';
import Swal from 'sweetalert2';
import Loading from '../../../../components/Loader/Loading';
import { formatDateTime } from '../../../../utils/dateUtils';
import { useDispatch } from 'react-redux';
import { InlineSectionPreview } from './MobilePreview';
import { quickBookModeLabel } from '../SectionItem/quickBookAction';

const sectionTypeColors = {
    horizontal_scroll: { bg: '#e3f2fd', color: '#1565c0' },
    carousel: { bg: '#f3e5f5', color: '#7b1fa2' },
    grid_2_column: { bg: '#e8f5e9', color: '#2e7d32' },
    grid_3_column: { bg: '#fff3e0', color: '#e65100' },
    grid_4_column: { bg: '#fce4ec', color: '#c2185b' },
    fixed_banner: { bg: '#e0f7fa', color: '#00838f' },
    banner: { bg: '#ede7f6', color: '#5e35b1' },
    vertical_list: { bg: '#f1f8e9', color: '#558b2f' },
    mixed_grid: { bg: '#ede7f6', color: '#512da8' }
};

const backgroundTypeColors = {
    color: { bg: '#e3f2fd', color: '#1565c0' },
    image: { bg: '#f3e5f5', color: '#7b1fa2' },
    gradient: { bg: '#fff3e0', color: '#e65100' },
    none: { bg: '#f5f5f5', color: '#757575' }
};

const formatLabel = (value) => value
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, char => char.toUpperCase());

const formatValue = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    if (value === null || value === undefined || value === '') return 'N/A';
    if (Array.isArray(value)) return value.length ? value.map(formatValue).join(', ') : 'N/A';
    if (typeof value === 'object') {
        const entries = Object.entries(value);
        if (!entries.length) return 'N/A';
        return entries
            .map(([key, itemValue]) => `${formatLabel(key)}: ${formatValue(itemValue)}`)
            .join('  ·  ');
    }
    return String(value);
};

const DetailField = ({ label, value, xs = 12, sm = 6, md = 4, color }) => (
    <Grid item xs={xs} sm={sm} md={md}>
        <Typography sx={{ fontSize: '0.68rem', color: '#667085', mb: 0.25 }}>{label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 24 }}>
            {color && (
                <Box sx={{ width: 20, height: 20, flexShrink: 0, bgcolor: color, border: '1px solid #d0d5dd', borderRadius: 0.5 }} />
            )}
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                {formatValue(value)}
            </Typography>
        </Box>
    </Grid>
);

const DetailCard = ({ title, children }) => (
    <Card sx={{ p: 2, mt: 2 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, mb: 1.5 }}>{title}</Typography>
        <Grid container spacing={1.5}>{children}</Grid>
    </Card>
);

const ViewSection = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const menuDispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [sectionData, setSectionData] = useState({});
    const pageType = searchParams.get('pageType') || sectionData.displayOptions?.pageType || 'section';
    const entityLabel = pageType === 'header' ? 'Header' : pageType === 'footer' ? 'Footer' : 'Section';
    const sectionLayoutType = sectionData.displayOptions?.sectionType || sectionData.sectionType;
    const backLabel = pageType === 'header'
        ? 'Back to Header'
        : pageType === 'footer'
            ? 'Back to Footer'
            : 'Back to Section';
    const listPath = pageType === 'header'
        ? '/customer-app/header-management'
        : pageType === 'footer'
            ? '/customer-app/footer-management'
            : '/customer-app/sections';

    useEffect(() => {
        menuDispatch({
            type: 'menuSelect',
            payload: pageType === 'header' ? 'header-management' : pageType === 'footer' ? 'footer-management' : 'sections'
        });
    }, [menuDispatch, pageType]);

    useEffect(() => {
        if (id) {
            setIsLoading(true);
            const fetchSectionData = async () => {
                try {
                    const response = await SectionServices.getSectionById(id);
                    if (String(response.success) === '1') {
                        setSectionData(response.data);
                    }
                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: error.message || 'Failed to fetch section details',
                    });
                } finally {
                    setIsLoading(false);
                }
            };
            fetchSectionData();
        }
    }, [id]);

    const renderActionData = (item) => {
        const actionData = item.actionData || {};

        if (!item.actionType || item.actionType === 'none') return 'N/A';
        if (item.actionType === 'navigate') return actionData.route || 'N/A';
        if (item.actionType === 'open_service') return actionData.serviceId || 'N/A';
        if (item.actionType === 'open_category') return actionData.categoryId || 'N/A';
        if (item.actionType === 'open_url') return actionData.url || 'N/A';
        if (item.actionType === 'open_quick_book') {
            const service = actionData.quickBookServiceId || 'All services in this mode';
            return `${quickBookModeLabel(actionData.mode)} · ${service}`;
        }
        if (item.actionType === 'show_popup') return actionData.content || actionData.popupId || 'N/A';

        return JSON.stringify(actionData);
    };

    const renderCompleteDetails = () => {
        const options = sectionData.displayOptions || {};
        const knownOptionKeys = new Set([
            'sectionType', 'pageType', 'requiresServiceMapping', 'isNotVisible',
            'showTitle', 'titlePosition', 'titleColor', 'titleFontFamily', 'titleFontSize', 'titleFontWeight', 'titleFontItalic', 'titleTextAlign',
            'showSubTitle', 'subTitlePosition', 'subTitleColor', 'subTitleFontFamily', 'subTitleFontSize', 'subTitleFontWeight', 'subTitleFontItalic', 'subTitleTextAlign',
            'enableSubTitleBackgroundColor', 'subTitleBackgroundColor',
            'showDescription', 'descriptionPosition', 'descriptionColor', 'descriptionFontFamily', 'descriptionFontSize', 'descriptionFontWeight', 'descriptionFontItalic', 'descriptionTextAlign',
            'customFontName', 'customFontUrl', 'headingSubtextSpacing', 'viewAllFontSize', 'viewAllFontWeight', 'bookAgainTimeGap',
            'autoScrollDuration', 'showPagination', 'aspectRatio', 'cardWidth', 'cardHeight', 'sectionPadding', 'sectionMargin',
            'gradientStartColor', 'gradientEndColor', 'actionType', 'actionData'
        ]);
        const additionalOptions = Object.entries(options).filter(([key]) => !knownOptionKeys.has(key));

        return (
            <>
                <DetailCard title="Title">
                    <DetailField label="Title" value={sectionData.title} xs={12} sm={12} md={12} />
                    <DetailField label="Show Title" value={options.showTitle} />
                    <DetailField label="Position" value={options.titlePosition} />
                    <DetailField label="Text Alignment" value={options.titleTextAlign} />
                    <DetailField label="Color" value={options.titleColor} color={options.titleColor} />
                    <DetailField label="Font Family" value={options.titleFontFamily} />
                    <DetailField label="Font Size (px)" value={options.titleFontSize} />
                    <DetailField label="Font Weight" value={options.titleFontWeight} />
                    <DetailField label="Italic" value={options.titleFontItalic} />
                </DetailCard>

                <DetailCard title="Subtitle">
                    <DetailField label="Subtitle" value={sectionData.subtitle} xs={12} sm={12} md={12} />
                    <DetailField label="Show Subtitle" value={options.showSubTitle} />
                    <DetailField label="Position" value={options.subTitlePosition} />
                    <DetailField label="Text Alignment" value={options.subTitleTextAlign} />
                    <DetailField label="Color" value={options.subTitleColor} color={options.subTitleColor} />
                    <DetailField label="Font Family" value={options.subTitleFontFamily} />
                    <DetailField label="Font Size (px)" value={options.subTitleFontSize} />
                    <DetailField label="Font Weight" value={options.subTitleFontWeight} />
                    <DetailField label="Italic" value={options.subTitleFontItalic} />
                    <DetailField label="Highlight Enabled" value={options.enableSubTitleBackgroundColor} />
                    <DetailField label="Highlight Color" value={options.subTitleBackgroundColor} color={options.subTitleBackgroundColor} />
                </DetailCard>

                <DetailCard title="Description">
                    <DetailField label="Description" value={sectionData.description} xs={12} sm={12} md={12} />
                    <DetailField label="Show Description" value={options.showDescription} />
                    <DetailField label="Position" value={options.descriptionPosition} />
                    <DetailField label="Text Alignment" value={options.descriptionTextAlign} />
                    <DetailField label="Color" value={options.descriptionColor} color={options.descriptionColor} />
                    <DetailField label="Font Family" value={options.descriptionFontFamily} />
                    <DetailField label="Font Size (px)" value={options.descriptionFontSize} />
                    <DetailField label="Font Weight" value={options.descriptionFontWeight} />
                    <DetailField label="Italic" value={options.descriptionFontItalic} />
                </DetailCard>

                <DetailCard title="Page & Visibility">
                    <DetailField label="Page Type" value={options.pageType || pageType} />
                    <DetailField label="Requires Service Mapping" value={options.requiresServiceMapping ?? true} />
                    <DetailField label="Show Without Service" value={options.requiresServiceMapping === false} />
                    <DetailField label="Not Visible" value={options.isNotVisible === true} />
                    <DetailField label="Action Type" value={options.actionType} />
                    <DetailField label="Action Data" value={options.actionData} sm={12} md={6} />
                </DetailCard>

                <DetailCard title="Layout, Size & Spacing">
                    <DetailField label="Aspect Ratio" value={options.aspectRatio} />
                    <DetailField label="Card Width" value={options.cardWidth} />
                    <DetailField label="Card Height" value={options.cardHeight} />
                    <DetailField label="Heading–Subtext Space (px)" value={options.headingSubtextSpacing} />
                    <DetailField label="View All Font Size (px)" value={options.viewAllFontSize} />
                    <DetailField label="View All Font Weight" value={options.viewAllFontWeight} />
                    <DetailField label="Text Gap (px)" value={options.bookAgainTimeGap} />
                    <DetailField label="Section Padding" value={options.sectionPadding} sm={12} md={6} />
                    <DetailField label="Section Margin" value={options.sectionMargin} sm={12} md={6} />
                </DetailCard>

                <DetailCard title="Background & Media">
                    <DetailField label="Background Color" value={sectionData.backgroundColor} color={sectionData.backgroundColor} />
                    <DetailField label="Gradient Start Color" value={options.gradientStartColor} color={options.gradientStartColor} />
                    <DetailField label="Gradient End Color" value={options.gradientEndColor} color={options.gradientEndColor} />
                    <DetailField label="Mobile Background File" value={sectionData.backgroundImage} sm={12} md={6} />
                    <DetailField label="Desktop Background File" value={sectionData.desktopBackgroundImage} sm={12} md={6} />
                    <DetailField label="Custom Font Name" value={options.customFontName} />
                    <DetailField label="Custom Font File" value={options.customFontUrl} sm={12} md={6} />
                </DetailCard>

                <DetailCard title="Behavior & Metadata">
                    <DetailField label="Auto Scroll Duration (seconds)" value={options.autoScrollDuration ?? sectionData.autoScrollDuration} />
                    <DetailField label="Show Pagination" value={options.showPagination} />
                    <DetailField label="ID" value={sectionData.id} />
                    <DetailField label="Created By" value={sectionData.createdBy} />
                    <DetailField label="Updated By" value={sectionData.updatedBy} />
                    <DetailField label="Updated On" value={sectionData.updatedAt ? formatDateTime(sectionData.updatedAt) : null} />
                    <DetailField label="Deleted On" value={sectionData.deletedAt ? formatDateTime(sectionData.deletedAt) : null} />
                </DetailCard>

                {additionalOptions.length > 0 && (
                    <DetailCard title="Additional Display Options">
                        {additionalOptions.map(([key, value]) => (
                            <DetailField key={key} label={formatLabel(key)} value={value} sm={12} md={6} />
                        ))}
                    </DetailCard>
                )}
            </>
        );
    };

    const renderItems = () => {
        const items = Array.isArray(sectionData.items) ? sectionData.items : [];

        return (
            <Card sx={{ p: 2, mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>{entityLabel} Items</Typography>
                    <Chip size="small" label={`${items.length} item${items.length === 1 ? '' : 's'}`} />
                </Box>

                {items.length === 0 ? (
                    <Box sx={{ py: 3, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e5e7eb' }}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#667085' }}>No items created</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={1.5}>
                        {items.map((item, index) => (
                            <Grid item xs={12} key={item.id || index}>
                                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 1.5, bgcolor: item.isActive ? '#fff' : '#f9fafb' }}>
                                    <Grid container spacing={1.5} alignItems="flex-start">
                                        <Grid item xs={12} sm={2}>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    aspectRatio: '1.6 / 1',
                                                    borderRadius: 1,
                                                    border: '1px solid #e5e7eb',
                                                    bgcolor: '#f3f4f6',
                                                    overflow: 'hidden',
                                                    display: 'grid',
                                                    placeItems: 'center'
                                                }}
                                            >
                                                {item.imageUrl || item.thumbnailUrl ? (
                                                    <img
                                                        src={item.imageUrl || item.thumbnailUrl}
                                                        alt={item.title || `${entityLabel} item`}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af' }}>No image</Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={10}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                                                <Typography sx={{ fontSize: '0.86rem', fontWeight: 700 }}>
                                                    {index + 1}. {item.title || 'Untitled'}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={item.isActive ? 'Active' : 'Inactive'}
                                                    sx={{
                                                        height: 20,
                                                        fontSize: '0.65rem',
                                                        bgcolor: item.isActive ? '#dcfce7' : '#fee2e2',
                                                        color: item.isActive ? '#166534' : '#991b1b'
                                                    }}
                                                />
                                                {item.badgeText && (
                                                    <Chip
                                                        size="small"
                                                        label={`Badge: ${item.badgeText}`}
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.65rem',
                                                            bgcolor: item.badgeColor || '#e5e7eb',
                                                            color: item.displayOptions?.badgeTextColor || '#111827'
                                                        }}
                                                    />
                                                )}
                                            </Box>

                                            <Grid container spacing={1}>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography sx={{ fontSize: '0.68rem', color: '#667085' }}>Subtitle</Typography>
                                                    <Typography sx={{ fontSize: '0.76rem', wordBreak: 'break-word' }}>{item.subtitle || 'N/A'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography sx={{ fontSize: '0.68rem', color: '#667085' }}>Action</Typography>
                                                    <Typography sx={{ fontSize: '0.76rem', wordBreak: 'break-word' }}>
                                                        {item.actionType?.replace(/_/g, ' ') || 'N/A'}: {renderActionData(item)}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography sx={{ fontSize: '0.68rem', color: '#667085' }}>Description</Typography>
                                                    <Typography sx={{ fontSize: '0.76rem', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                                        {item.description || 'N/A'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Typography sx={{ fontSize: '0.68rem', color: '#667085' }}>Position</Typography>
                                                    <Typography sx={{ fontSize: '0.76rem' }}>{item.position || 'N/A'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Typography sx={{ fontSize: '0.68rem', color: '#667085' }}>Start Date/Time</Typography>
                                                    <Typography sx={{ fontSize: '0.76rem' }}>{item.startDateTime ? formatDateTime(item.startDateTime) : 'N/A'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Typography sx={{ fontSize: '0.68rem', color: '#667085' }}>End Date/Time</Typography>
                                                    <Typography sx={{ fontSize: '0.76rem' }}>{item.endDateTime ? formatDateTime(item.endDateTime) : 'N/A'}</Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Card>
        );
    };

    return (
        <>
            {isLoading && <Loading />}
            <PageHeaderLayout headerTitle={`${entityLabel} Details`}>
                <Button variant='contained' onClick={() => navigate(listPath)} sx={{ height: "30px", marginTop: "10px", textTransform: 'none' }} startIcon={<ArrowBack />}>
                    {backLabel}
                </Button>
                <Button variant='outlined' onClick={() => navigate(`/customer-app/home-screen/section-items/${id}?pageType=${pageType}`)} sx={{ height: "30px", marginTop: "10px", textTransform: 'none' }} startIcon={<ListAlt />}>
                    {entityLabel} Items
                </Button>
                <PermissionGuard feature='customer-app' permission='canCreate'>
                    <Button variant='contained' color='success' onClick={() => navigate(`/customer-app/home-screen/section-item/0?sectionId=${id}&pageType=${pageType}`)} sx={{ height: "30px", marginTop: "10px", textTransform: 'none' }} startIcon={<Add />}>
                        Create Item
                    </Button>
                </PermissionGuard>
            </PageHeaderLayout>
            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    {renderCompleteDetails()}
                    <Card sx={{ p: 2, mt: 2 }}>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, mb: 1.5 }}>Section Settings</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Section Type</Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    {sectionLayoutType && (
                                        <Chip
                                            label={sectionLayoutType.replace(/_/g, ' ')}
                                            size="small"
                                            sx={{
                                                backgroundColor: sectionTypeColors[sectionLayoutType]?.bg || '#f5f5f5',
                                                color: sectionTypeColors[sectionLayoutType]?.color || '#757575',
                                                fontSize: '0.75rem',
                                                textTransform: 'capitalize',
                                                fontWeight: 500
                                            }}
                                        />
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Position</Typography>
                                <TextField
                                    disabled
                                    value={sectionData.position || ''}
                                    fullWidth
                                    size="small"
                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Background Type</Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={sectionData.backgroundType || 'none'}
                                        size="small"
                                        sx={{
                                            backgroundColor: backgroundTypeColors[sectionData.backgroundType]?.bg || '#f5f5f5',
                                            color: backgroundTypeColors[sectionData.backgroundType]?.color || '#757575',
                                            fontSize: '0.75rem',
                                            textTransform: 'capitalize',
                                            fontWeight: 500
                                        }}
                                    />
                                </Box>
                            </Grid>
                            {sectionData.backgroundType === 'color' && (
                                <Grid item xs={12} sm={6}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Background Color</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{
                                            width: 24,
                                            height: 24,
                                            backgroundColor: sectionData.backgroundColor || '#FFFFFF',
                                            border: '1px solid #ddd',
                                            borderRadius: 0.5
                                        }} />
                                        <Typography sx={{ fontSize: '0.75rem' }}>
                                            {sectionData.backgroundColor || '#FFFFFF'}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Show View All</Typography>
                                <TextField
                                    disabled
                                    value={sectionData.showViewAll ? 'Yes' : 'No'}
                                    fullWidth
                                    size="small"
                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                />
                            </Grid>
                            {sectionData.showViewAll && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>View All Text</Typography>
                                        <TextField
                                            disabled
                                            value={sectionData.viewAllText || ''}
                                            fullWidth
                                            size="small"
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>View All Route</Typography>
                                        <TextField
                                            disabled
                                            value={sectionData.viewAllRoute || ''}
                                            fullWidth
                                            size="small"
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Start Date/Time</Typography>
                                <Typography sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                    {sectionData.startDateTime ? formatDateTime(sectionData.startDateTime) : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>End Date/Time</Typography>
                                <Typography sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                    {sectionData.endDateTime ? formatDateTime(sectionData.endDateTime) : 'N/A'}
                                </Typography>
                            </Grid>
                            {['carousel', 'banner'].includes(sectionLayoutType) && (
                                <Grid item xs={12} sm={6}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Auto Scroll</Typography>
                                    <TextField
                                        disabled
                                        value={sectionData.displayOptions?.autoScrollDuration ? `${sectionData.displayOptions.autoScrollDuration} seconds` : 'False'}
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    />
                                </Grid>
                            )}
                            {sectionLayoutType === 'banner' && (
                                <Grid item xs={12} sm={6}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Show Pagination</Typography>
                                    <TextField
                                        disabled
                                        value={sectionData.displayOptions?.showPagination === false ? 'No' : 'Yes'}
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Max Items To Show</Typography>
                                <TextField
                                    disabled
                                    value={sectionData.maxItemsToShow || ''}
                                    fullWidth
                                    size="small"
                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Status</Typography>
                                <Chip
                                    label={sectionData.isActive ? 'Active' : 'Inactive'}
                                    size="small"
                                    sx={{
                                        backgroundColor: sectionData.isActive ? '#e8f5e9' : '#ffebee',
                                        color: sectionData.isActive ? '#2e7d32' : '#c62828',
                                        fontWeight: 500
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Created On</Typography>
                                <Typography sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                    {sectionData.createdAt ? formatDateTime(sectionData.createdAt) : 'N/A'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Card>
                    {renderItems()}
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ p: 2 }}>
                        <InlineSectionPreview formData={sectionData} sectionId={id} />
                        <Box sx={{ mt: 2 }}>
                            <PermissionGuard feature='customer-app' permission='canEdit'>
                                <Button
                                    onClick={() => navigate(`/customer-app/home-screen/section/${id}?pageType=${pageType}`)}
                                    variant="contained"
                                    color="warning"
                                    size="small"
                                    fullWidth
                                    startIcon={<Edit />}
                                >
                                    Edit {entityLabel}
                                </Button>
                            </PermissionGuard>
                        </Box>
                    </Card>
                </Grid>
            </Grid>
        </>
    );
};

export default ViewSection;
